import React, { useEffect, useMemo, useRef, useState } from "react";
import { wormColors } from "core/src/core/constants.js";
import { flagUrlSquare } from "../../../shared/flags";
import { SIZE_LABEL, ordinal } from "../../../online/dynamic";
import type { NextRound, RoundResult, RoundSetup } from "../../../online/dynamic";
import type { DynamicLayerProps, PublicPlayer } from "./types";
import NextRoundReveal, { BannerOut, PASS_LEAD_MS, revealHasResult, revealLeadMs, revealStraddles, revealVariant, royaleStandingsFor, useStraddlingReveal } from "./NextRoundReveal";
import { CutLine, FinalBadge, KnockoutBadge, StageRule, Wall, seatOrder } from "./RoyaleMoments";
import m from "./RoyaleMoments.module.scss";
import s from "./RoyaleLayer.module.scss";
import { useSoundOn } from "../../../audio/use-sound";
import RoyaleEnd from "./RoyaleEnd";

// The Royale's screens over the game (sketch D71: gold, the crown).
//   playing    the slim whole-Royale track (top centre) and the "Out" line that
//              fills as players crash (top right); in the final only a small tag
//   countdown  who went out (a band over the lap), then "the map shrinks" or the
//              final's versus screen when the stage changes, then the next
//              round's reveal for the last part of the pause
//   out        the "You are out" card, kept until dismissed, then a small chip
//   over       the crown for the winner, the placing for everybody else
// The existing game chrome keeps the round readout, the scoreboard and its own
// leave button; nothing of that is drawn here.

// What the winner gets (wins counter, achievement box, eggs) is wired later:
// pass it as `reward` and it shows under the winner's name. Nothing is shown
// until then.
export interface RoyaleLayerProps extends DynamicLayerProps {
  reward?: React.ReactNode;
}

// The end of every pause belongs to the next round's reveal. Two designs stay
// on into the next round (the pass-through and the banner): those are not the
// pause's to show, the layer runs them with useStraddlingReveal. For the
// pass-through the pause only clears the stage at its very end.
const PASS = revealVariant() === "pass";
const BANNER = revealHasResult(revealVariant()); // the banner, or a board: the pause's one band: who is out, then the next round
const REVEAL_MS = PASS ? PASS_LEAD_MS : 3400;   // the pause's own screens end this long before the pause does
// When the reveal begins, by its design's length. A longer one (the banner)
// begins while the pause's screens are still up and sits above them.
const STRADDLES = revealStraddles(revealVariant());
const REVEAL_LEAD = revealLeadMs(revealVariant());
const VS_OUT_MS = 700;   // the final's versus screen: its way out (the stylesheet's .vsOut)
const BAND_MS = 3000;     // "who is out", before a longer pause's middle part
// After a stage's last round (the server's STAGE_PAUSE_MS is 14 s): the cut
// line, then the wall until here (its cards are in and the out ones dropped by
// about 4.4 s into it, and it stays 3 s more: their fall, then the stage ahead,
// its first round included), and the round starts just after it.
const STAGE_CUT_MS = 6000;
const STAGE_WALL_END_MS = 13600;
const MOMENT_FADE_MS = 550; // (RoyaleMoments .fadeOut / .leaving)
// The final's versus screen: which of the designs (see PauseScreens).
function vsVariant(): number {
  let v = 0;
  try { v = Number(new URLSearchParams(window.location.search).get("vs") || window.localStorage.getItem("royaleVs")); } catch { /* no storage */ }
  return v >= 1 && v <= 5 ? v : 1;
}
const FALLBACK_COLOR = "#28c4d4";

// The Royale's stages (edge-server directors/royale.js): the field at the start
// of each one, the two finalists last. The server sends it with every round
// (setup.fields); this is the same plan for a room that has not said yet.
const FIELDS_30 = [30, 23, 17, 12, 8, 5, 3, 2];

const cssVar = (o: Record<string, string | number>) => o as React.CSSProperties;
const colorOf = (p: PublicPlayer | undefined | null) => (p && (wormColors as Record<string, string>)[p.colorId]) || FALLBACK_COLOR;
const flagOf = (p: PublicPlayer | undefined | null) => flagUrlSquare((p && p.flag) || "xx");
const goOut = (n: number, when: string) => (n === 1 ? `1 goes out ${when}` : `${n} go out ${when}`);

// ---------- icons (24 grid, 2px strokes) ----------
const svgProps = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
const CrownI = () => (<svg {...svgProps}><path d="M4 8l4 3.5L12 5l4 6.5L20 8l-1.6 10H5.6L4 8z" fill="currentColor" fillOpacity="0.25" /><path d="M6 21h12" /></svg>);
const Eye = () => (<svg {...svgProps}><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>);
const Check = () => (<svg {...svgProps} strokeWidth={2.6}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>);

// ---------- timing ----------
// Which part of the pause we are in: the number of `cuts` (ms into the pause)
// already passed. Worked out from the clock, so a remount lands in the right place.
function usePausePart(startedAt: number, cuts: number[]): number {
  const key = cuts.join(",");
  const partNow = () => { const t = Date.now() - startedAt; return cuts.filter((c) => t >= c).length; };
  const [part, setPart] = useState(partNow);
  useEffect(() => {
    setPart(partNow());
    const timers = cuts
      .map((c) => startedAt + c - Date.now())
      .filter((ms) => ms > 0)
      .map((ms) => setTimeout(() => setPart(partNow()), ms + 15));
    return () => timers.forEach(clearTimeout);
  }, [startedAt, key]);
  return part;
}

// Who has crashed so far this round, in order (polled: the game state is mutated in place).
function useCrashed(getGameState: () => any, limit: number, roundKey: string): string[] {
  const [ids, setIds] = useState<string[]>([]);
  useEffect(() => {
    const read = () => {
      const out: string[] = [];
      const events: any[] = getGameState()?.gameEvents || [];
      for (const ev of events) {
        if (out.length >= limit) break;
        if (ev && ev.type === "player_died" && ev.playerId && out.indexOf(ev.playerId) === -1) out.push(ev.playerId);
      }
      setIds((prev) => (prev.join() === out.join() ? prev : out));
    };
    read();
    const t = setInterval(read, 250);
    return () => clearInterval(t);
  }, [limit, roundKey]);
  return ids;
}

// ---------- 1. during a round ----------
// The way through the Royale: the field at each stage, and a tick a round
// between them (done, the one being played, to come).
function Track({ fields, stageNo, stageRound, stageRounds, final }: { fields: number[]; stageNo: number; stageRound: number; stageRounds: number; final: boolean }) {
  const stages = fields.length - 1;
  return (
    <div className={s.track} aria-hidden="true">
      {Array.from({ length: stages }, (_, k) => {
        const no = k + 1, done = final || no < stageNo, on = !final && no === stageNo;
        return (
          <React.Fragment key={no}>
            <b className={`${s.trNum} ${done ? s.trNumDone : ""}`}>{fields[k]}</b>
            <span className={`${s.trGrp} ${on ? s.trGrpOn : ""}`}>
              <span className={s.trTicks} style={cssVar({ "--h": "9px" })}>
                {Array.from({ length: stageRounds }, (_, r) => <i key={r} className={done || (on && r + 1 < stageRound) ? s.tickDone : on && r + 1 === stageRound ? s.tickNow : ""} />)}
              </span>
            </span>
          </React.Fragment>
        );
      })}
      <b className={`${s.trNum} ${final ? s.trNumDone : ""}`}>{fields[stages]}</b>
      <span className={`${s.trGrp} ${s.trFinal} ${final ? s.trGrpOn : ""}`}><CrownI /><small>Final</small></span>
    </div>
  );
}

// "7 go out after round 3", or "this round" on a stage's last.
const cutLine = (r: { out?: number; stageRound?: number; stageRounds?: number }) =>
  goOut(r.out || 0, (r.stageRound || 1) >= (r.stageRounds || 3) ? "this round" : `after round ${r.stageRounds || 3}`);

function RoundHud({ setup }: { setup: RoundSetup }) {
  return (
    <div className={s.top}>
      <Track fields={setup.fields || FIELDS_30} stageNo={setup.stageNo || 1} stageRound={setup.stageRound || 1} stageRounds={setup.stageRounds || 3} final={false} />
      <span className={s.topInfo}>
        <span className={s.topLeft}>Stage <b>{setup.stageNo || 1}</b> · Round <b>{setup.stageRound || 1}</b>/{setup.stageRounds || 3}</span>
        <span className={s.topOut}>{cutLine(setup)}</span>
      </span>
    </div>
  );
}

// ---------- 2. the pause between rounds ----------
function RunBar({ label, sub, ms, startedAt }: { label: string; sub?: string; ms: number; startedAt: number }) {
  // the bar runs with the clock: a remount picks it up where it is
  const [delay] = useState(() => Math.min(0, startedAt - Date.now()));
  return (
    <div className={s.nb}>
      <span>{label}</span>
      <i><u style={{ animationDuration: `${Math.max(300, ms)}ms`, animationDelay: `${delay}ms` }} /></i>
      {sub && <small>{sub}</small>}
    </div>
  );
}

// The words of the next round's reveal.
function revealWords(next: NextRound, _number: number | undefined, finalWins: number): { kicker?: string; line?: string } {
  // Just the mode: what kind of round it is sits on the card as a badge (nextBadge).
  return {};
}

// The next round's badge on its card: the final's. (A stage's last round has
// none: the steps right above the next round show the knockout.)
function nextBadge(next: NextRound, finalWins: number): React.ReactNode {
  return next.final ? <FinalBadge wins={finalWins} /> : undefined;
}

function Reveal({ next, kicker, line, endsAt }: { next: NextRound; kicker: string; line: string; endsAt: number }) {
  const [durationMs] = useState(() => Math.max(0, endsAt - Date.now()));
  if (!next.mode || !next.shape || !next.sizeKey || durationMs < 300) return null;
  return <NextRoundReveal mode={next.mode} shape={next.shape} sizeKey={next.sizeKey} context="royale" kicker={kicker} line={line} durationMs={durationMs} />;
}

function Pips({ n, of, fresh }: { n: number; of: number; fresh: boolean }) {
  return (
    <span className={s.pips}>
      {Array.from({ length: of }, (_, k) => <i key={k} className={`${k < n ? s.pipOn : ""} ${fresh && k === n - 1 ? s.pipFresh : ""}`} />)}
    </span>
  );
}

interface PauseProps {
  setup: RoundSetup; next: NextRound | null; result: RoundResult | null;
  players: PublicPlayer[]; who: (id: string) => PublicPlayer | undefined;
  score: Record<string, number>; roundWinnerId: string | null;
  seatIds: string[]; youIds: string[]; pauseStartedAt: number; pauseMs: number;
}
function Pause(props: PauseProps) {
  const { setup, next, pauseStartedAt, pauseMs } = props;
  const revealing = usePausePart(pauseStartedAt, [Math.max(0, pauseMs - REVEAL_LEAD), pauseMs]) === 1;
  const words = next && next.mode && !STRADDLES ? revealWords(next, setup.number, next.finalWins || setup.finalWins || 3) : null;
  return (
    <>
      <PauseScreens {...props} />
      {revealing && words && <Reveal key={pauseStartedAt} next={next!} kicker={words.kicker} line={words.line} endsAt={pauseStartedAt + pauseMs} />}
    </>
  );
}
function PauseScreens({ setup, next, result, players, who, score, roundWinnerId, seatIds, youIds, pauseStartedAt, pauseMs }: PauseProps) {
  const number = setup.number;
  const out = (result && result.out) || [];
  const finalWins = (next && next.finalWins) || setup.finalWins || 3;
  const finalIntro = !!(next && next.final && !setup.final);
  const shrinks = false; // (the wall of who is left says the stage is over; it has its own time, see the layer)
  const hasReveal = !!(next && next.mode);
  const revealAt = hasReveal ? Math.max(0, pauseMs - REVEAL_MS) : pauseMs;
  const firstEnd = finalIntro || shrinks ? Math.min(BAND_MS, revealAt) : revealAt;
  const part = usePausePart(pauseStartedAt, [firstEnd, revealAt, pauseMs]);
  // the versus screen leaves as it came: its last moments are its way out
  const vsLeaving = usePausePart(pauseStartedAt, [revealAt - VS_OUT_MS]) === 1;
  useSoundOn(part === 1 && finalIntro, "royale-final");
  const mine = (id: string) => seatIds.indexOf(id) !== -1;
  // me (or the first of the two) on the left
  const duo = (list: PublicPlayer[]) => { const two = list.slice(0, 2); return two[1] && mine(two[1].id) && !mine(two[0].id) ? [two[1], two[0]] : two; };

  if (part >= 2) return null; // the reveal's time (Pause shows it), then the round is about to start

  // the map shrinks
  if (part === 1 && shrinks && next) {
    const stadium = (next.shape || setup.shape) === "Stadium";
    const rx = stadium ? 86 : 10;
    return (
      <div className={`${s.moment} ${s.dimmed}`} style={cssVar({ "--dim": 0.74 })}>
        <div className={s.shr}>
          <div className={s.shrText}>
            <span className={s.kicker}>Stage {setup.stageNo || 1} over</span>
            <h2><em>{next.alive} left.</em><br />The map shrinks.</h2>
            <span className={s.shrRule}><b>{next.out}</b> {next.out === 1 ? "goes" : "go"} out after {next.stageRounds || 3} rounds</span>
          </div>
          <div className={s.shrMap}>
            <svg viewBox="0 0 320 200" aria-hidden="true">
              <rect x="6" y="14" width="308" height="172" rx={rx} fill="none" stroke="rgba(233,243,255,0.3)" strokeWidth="1.5" strokeDasharray="5 6" />
              <g className={s.shrInner}>
                <rect x="6" y="14" width="308" height="172" rx={rx} fill="rgba(255,210,63,0.08)" stroke="#ffd23f" strokeWidth="3" vectorEffect="non-scaling-stroke" />
                {Array.from({ length: Math.min(next.alive || 0, 20) }, (_, i) => {
                  const a = i * 2.4, r = 0.25 + ((i * 37) % 60) / 100;
                  return <circle key={i} cx={160 + Math.cos(a) * 118 * r} cy={100 + Math.sin(a) * 62 * r} r="3.2" fill={`hsl(${i * 24 + 20}, 88%, 64%)`} />;
                })}
              </g>
            </svg>
            <span className={s.shrFrom}>{SIZE_LABEL[setup.sizeKey]}</span>
            <span className={s.shrTo}>{SIZE_LABEL[next.sizeKey || ""] || ""}</span>
          </div>
        </div>
      </div>
    );
  }

  // the final's versus screen
  if (part === 1 && finalIntro) {
    const [a, b] = duo(players.filter((p) => out.indexOf(p.id) === -1));
    if (!a || !b) return null;
    // (five designs to choose between: ?vs=1..5 or localStorage royaleVs; /debug/royale-flow sets it)
    const v = vsVariant();
    const straight = v === 5;
    return (
      <div className={`${s.moment} ${s.vs} ${s["vs" + v] || ""} ${vsLeaving ? s.vsOut : ""}`}>
        <div className={`${s.vsPanel} ${s.vsPanelL}`} style={cssVar({ "--c": colorOf(a) })} />
        <div className={`${s.vsPanel} ${s.vsPanelR}`} style={cssVar({ "--c": colorOf(b) })} />
        {v !== 4 && <svg className={s.vsSeam} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"><path d={straight ? "M50 0L50 100" : "M55 0L45 100"} stroke={v === 2 ? "#fff" : "#ffd23f"} strokeWidth="2" vectorEffect="non-scaling-stroke" /></svg>}
        <div className={s.vsFlash} />
        <div className={s.vsStage}>
          {[a, b].map((p, i) => (
            <div key={p.id} className={`${s.vsP} ${i === 0 ? s.vsL : s.vsR}`} style={cssVar({ "--c": colorOf(p) })}>
              <span className={s.vsAva}><img src={flagOf(p)} alt="" /></span>
              <b>{p.name}</b>
            </div>
          ))}
          <div className={s.vsCore}>
            {v === 5 ? <FinalBadge wins={finalWins} className={s.vsPlate} /> : v === 4 ? null : <h1 className={s.vsFinal}>FINAL</h1>}
            <span className={s.vsBall}>
              {v === 3 && <i className={s.vsRing} />}
              {v === 4 && <small><CrownI />Final</small>}
              <b>VS</b>
            </span>
            {v !== 5 && <span className={s.vsRule}>First to {finalWins}</span>}
          </div>
        </div>
      </div>
    );
  }
  if (part === 1) return null;

  // between the final's rounds: the score
  if (setup.final) {
    const [a, b] = duo(players);
    if (!a || !b) return null;
    const sa = score[a.id] || 0, sb = score[b.id] || 0;
    const pointA = sa === finalWins - 1, pointB = sb === finalWins - 1;
    const note = "";
    return (
      <div className={s.moment}>
        <div className={s.fs}>
          <span className={s.kicker}>Final score <i>First to {finalWins}</i></span>
          <div className={s.fsRow}>
            <div className={s.fsP} style={cssVar({ "--c": colorOf(a) })}>
              <img src={flagOf(a)} alt="" />
              <span><b>{a.name}{mine(a.id) && <em>You</em>}</b><Pips n={sa} of={finalWins} fresh={roundWinnerId === a.id} /></span>
            </div>
            <div className={s.fsScore}><b className={roundWinnerId === a.id ? s.fsNew : ""}>{sa}</b><i>:</i><b className={roundWinnerId === b.id ? s.fsNew : ""}>{sb}</b></div>
            <div className={`${s.fsP} ${s.fsRight}`} style={cssVar({ "--c": colorOf(b) })}>
              <span><b>{b.name}</b><Pips n={sb} of={finalWins} fresh={roundWinnerId === b.id} /></span>
              <img src={flagOf(b)} alt="" />
            </div>
          </div>
          {note && <div className={s.fsNote}><b>Match point</b>{note}</div>}
        </div>
      </div>
    );
  }

  // who is out: a band over the lap (the banner says it instead, in its first beat)
  if (!result || !result.royale || BANNER || out.length === 0) return null;
  const through = youIds.some((id) => out.indexOf(id) === -1);
  return (
    <div className={s.moment}>
      <div className={s.band}>
        <div className={s.bandIn}>
          <div className={s.bandRow}>
            <div className={s.roLeft}>
              <span className={s.kicker}>{result.stageOver ? `Stage ${setup.stageNo || 1} over` : `Round ${setup.stageRound || 1} over`}</span>
              <b className={s.roBig}>{result.alive}<small>left</small></b>
              {through && <span className={s.roMe}><i><Check /></i>You are through</span>}
            </div>
            <div className={s.roOut}>
              <span className={s.kicker}>Out</span>
              <div className={s.roChips}>
                {out.map((id, k) => {
                  const p = who(id);
                  const place = result.places && result.places[id];
                  return (
                    <span key={id} className={s.roChip} style={cssVar({ "--d": `${0.15 + k * 0.16}s` })}>
                      <img src={flagOf(p)} alt="" /><b><u>{p ? p.name : "Player"}</u></b>{place != null && <em>{ordinal(place)}</em>}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
          <RunBar label="Victory lap" ms={firstEnd} startedAt={pauseStartedAt} />
        </div>
      </div>
    </div>
  );
}

// ---------- buttons ----------
// ---------- the layer ----------
export default function RoyaleLayer(props: RoyaleLayerProps) {
  const { phase, setup, next, result, royaleResult, players, playerById, score, roundWinnerId, seatIds, youIds, myPlace, pauseStartedAt, pauseMs, getGameState, onLeave, onAgain, onPlayNow, reward, onResults } = props;
  const number = (royaleResult && royaleResult.number) || (setup && setup.number) || 0;
  // "Keep watching" holds for this Royale
  const [watchingFor, setWatchingFor] = useState<number | null>(null);
  const outIds = (result && result.royale && result.out) || [];
  // Everyone's seat on the walls, kept for the whole Royale: first in the
  // shuffled order the opening showed, a latecomer in the seat of the fill
  // player they took over, and a player out leaves the seat empty.
  const seatsRef = useRef<string[] | null>(null);
  const seenRef = useRef<Map<string, PublicPlayer>>(new Map());
  const goneRef = useRef<Set<string>>(new Set());
  players.forEach((p) => seenRef.current.set(p.id, p));
  outIds.forEach((id) => goneRef.current.add(id));
  if (!seatsRef.current && players.length) seatsRef.current = seatOrder(players, number).map((p) => p.id);
  else if (seatsRef.current) {
    const here = new Set(players.map((p) => p.id));
    for (const p of players) {
      if (seatsRef.current.indexOf(p.id) !== -1) continue;
      const free = seatsRef.current.findIndex((id) => !here.has(id) && !goneRef.current.has(id));
      if (free !== -1) seatsRef.current[free] = p.id; else seatsRef.current.push(p.id);
    }
  }
  const wallSeats = (seatsRef.current || []).map((id) => (players.some((p) => p.id === id) ? seenRef.current.get(id) || null : null));
  const roundWinnerP = phase === "countdown" && roundWinnerId ? playerById[roundWinnerId] || players.find((x) => x.id === roundWinnerId) : undefined;
  const straddlingReveal = useStraddlingReveal(
    phase === "countdown", pauseStartedAt, pauseMs,
    // (a new stage's first round is on its wall already: no next round card after it)
    next && next.mode && !(result && result.stageOver && !next.final) ? {
      mode: next.mode, context: "royale", ...revealWords(next, number, next.finalWins || (setup && setup.finalWins) || 3),
      badge: nextBadge(next, next.finalWins || (setup && setup.finalWins) || 3),
      // the pause's card is the cut line and who is near it (after a stage's last
      // round the layer shows it itself, then the wall: see below)
      result: BANNER && setup && !setup.final && players.length > 0 && !(result && result.stageOver)
        ? <CutLine players={players} score={score || {}} mine={seatIds} cut={setup.out || 0} round={setup.stageRound || 1} rounds={setup.stageRounds || 3} stageNo={setup.stageNo || 1} gone={null} before={props.scoreBefore} winner={roundWinnerP || null} />
        : undefined,
    } : null,
    getGameState,
  );

  const who = (id: string) => playerById[id] || players.find((p) => p.id === id);
  const mine = (id: string | null | undefined) => !!id && seatIds.indexOf(id) !== -1;
  const size = (royaleResult && royaleResult.size) || (setup && setup.size) || players.length;
  const winnerId = (royaleResult && royaleResult.winner) || (result && result.winner) || null;
  const over = phase === "over" || !!winnerId;
  // (hooks: all of them before the first return, the result's included)
  const stagePart = usePausePart(pauseStartedAt, [next && next.final ? BAND_MS : STAGE_CUT_MS, STAGE_WALL_END_MS]);
  // the cut line and the wall each fade out over their last moments (0 = up, 1 = fading)
  const cutFade = usePausePart(pauseStartedAt, [(next && next.final ? BAND_MS : STAGE_CUT_MS) - MOMENT_FADE_MS]);
  const wallFade = usePausePart(pauseStartedAt, [STAGE_WALL_END_MS - MOMENT_FADE_MS]);
  // A stage just ended: the cut line (who is out), then the wall where their
  // cards drop, then (the paced card's) next round. Into the final it is only
  // the cut line, and the versus screen takes over.
  const stageOver = phase === "countdown" && !!(result && result.stageOver);
  // (am I out: worked out here, ahead of the returns, for its sound)
  const outNow = (result && result.out) || [];
  const stillIn = (id: string) => players.some((p) => p.id === id) && outNow.indexOf(id) === -1;
  const outSeat = seatIds.find((id) => outNow.indexOf(id) !== -1);
  const place = myPlace != null ? myPlace : outSeat && result && result.places ? result.places[outSeat] : null;
  // (not before the wall has dropped my card)
  const iAmOut = seatIds.length > 0 && place != null && !seatIds.some(stillIn) && !(stageOver && stagePart < 2 && !(next && next.final));
  // The Royale's sounds: a cut, our win, our way out.
  useSoundOn(stageOver && stagePart === 0, "royale-cut");
  useSoundOn(over && mine(winnerId), "match-win");
  useSoundOn(iAmOut && !mine(winnerId), "match-lost");

  // ---------- 6. the result ----------
  // (RoyaleEnd: one panel for every way a Royale ends for you)
  if (over) {
    const winner = winnerId ? who(winnerId) : undefined;
    const common = { size, number, onAgain, onResults, onLeave };
    if (mine(winnerId)) {
      return (
        <div className={`${s.root} ${s.moment} ${s.dimmed}`} style={cssVar({ "--dim": 0.82 })}>
          <div className={s.rays} />
          <div className={s.confetti}>
            {Array.from({ length: 40 }, (_, i) => (
              <i key={i} style={cssVar({
                left: `${(i * 37 + 11) % 100}%`, "--dl": `${((i * 0.29) % 3.2).toFixed(2)}s`, "--du": `${3 + (i % 5) * 0.5}s`,
                "--w": `${5 + (i % 3) * 2}px`, "--r": `${(i * 53) % 360}deg`,
                background: ["#ffd23f", "#fff3b0", "#e0a21a", "#ffffff", "#ffd23f", "#19f0b0"][i % 6],
              })} />
            ))}
          </div>
          <RoyaleEnd kind="win" hero={winner} reward={reward} {...common} />
        </div>
      );
    }
    // the final's loser, before the placings have arrived: 2nd
    const inFinal = !!(setup && setup.final) && seatIds.some((id) => players.some((p) => p.id === id));
    const myFinalPlace = myPlace != null ? myPlace : inFinal ? 2 : null;
    const watchOnly = !seatIds.length || myFinalPlace == null;
    return (
      <div className={`${s.root} ${s.moment} ${s.dimmed}`} style={cssVar({ "--dim": watchOnly ? 0.7 : 0.78 })}>
        {watchOnly
          ? <RoyaleEnd kind="watch" hero={winner} {...common} />
          : <RoyaleEnd kind="placed" winner={winner} place={myFinalPlace} {...common} />}
      </div>
    );
  }

  if (!setup) return null;

  // ---------- 3. am I out? ----------
  const watching = watchingFor === number;


  if (iAmOut && !watching) {
    return (
      <div className={`${s.root} ${s.moment} ${s.dimmed}`} style={cssVar({ "--dim": 0.72 })}>
        <RoyaleEnd kind="out" place={place} size={size} number={number} onAgain={onAgain} onResults={onResults} onLeave={onLeave} onWatch={() => setWatchingFor(number)} />
      </div>
    );
  }

  return (
    <div className={s.root}>
      {phase === "playing" && !setup.final && setup.cut && <KnockoutBadge className={m.ko} />}
      {phase === "countdown" && (
        <Pause
          key={"pause" + pauseStartedAt}
          setup={setup} next={next} result={result} players={players} who={who} score={score} roundWinnerId={roundWinnerId}
          seatIds={seatIds} youIds={youIds} pauseStartedAt={pauseStartedAt} pauseMs={pauseMs}
        />
      )}
      {straddlingReveal}
      {/* (after the reveal in the page, so they sit over it) */}
      {stageOver && stagePart === 0 && (
        <div className={`${m.cutSolo} ${cutFade ? m.fadeOut : ""}`}>
          <CutLine players={players} score={score || {}} mine={seatIds} cut={setup.out || 0} round={setup.stageRound || 3} rounds={setup.stageRounds || 3} stageNo={setup.stageNo || 1} gone={outIds} before={props.scoreBefore} winner={roundWinnerP || null} />
        </div>
      )}
      {stageOver && stagePart === 1 && next && !next.final && (
        <Wall key={"wall" + pauseStartedAt} leaving={wallFade > 0} players={players} seats={wallSeats} mine={seatIds} out={outIds} kicker={`Stage ${next.stageNo || 2}`} title={`${next.alive} left`} rule={<StageRule rounds={next.stageRounds || 3} out={next.out || 0} reset first={{ mode: next.mode, size: next.sizeKey, from: setup.sizeKey }} />} />
      )}
      {phase === "playing" && setup.final && <FinalBadge className={m.ko} wins={setup.finalWins || 3} />}
      {iAmOut && (
        <div className={s.watch}>
          <Eye /><span>You placed {ordinal(place!)} · watching</span>
          {onResults && <button onClick={onResults}>Results</button>}
          <button onClick={onLeave}>Leave</button>
        </div>
      )}
      {/* Arrived just after the start: a seat opens for you at the next round. */}
      {!iAmOut && props.pending && youIds.length === 0 && (
        <div className={s.watch}>
          <Eye /><span>You play from the next round</span>
        </div>
      )}
    </div>
  );
}
